import React, { useEffect, useRef, useState } from 'react';
import { useParams, useHistory, Prompt } from 'react-router-dom';
import { Alert, Badge, Card, Row, Col, Button, Form, Table, Modal } from 'react-bootstrap';
import { Page, Terminal } from 'components';
import Header from '../../../../components/Header/Header';
import dateformat from 'utils/dateformat';
import { getToken } from 'actions';
import {
  buildEquipmentUpdateSteps,
  getStepTimeoutMs,
  PM2_RESTART_WAIT_MS,
  POST_REBOOT_STEP,
} from '../../constants/equipmentUpdateSteps';
import {
  isCommandResponseComplete,
  joinResponseChunks,
} from '../../utils/equipmentCommandWait';
import {
  canAdvanceAfterResponse,
  parseEquipmentUpdateOutput,
} from '../../utils/equipmentUpdateParser';
import { canUseSupportCalibrar } from '../../supportAccess';
import './Conected.css';

const wsUrl = process.env.REACT_APP_API_MONITORA.replace('https', 'wss').replace('http', 'ws');
const refreshRate = 15000;
const resetPasswordCommand = process.env.REACT_APP_SUPPORT_RESET_PASSWORD_CMD;
const TERMINAL_COMMAND_TIMEOUT_MS = 30000;

const UPDATE_LEAVE_MESSAGE = 'A atualização do equipamento está em andamento. Aguarde até a conclusão do processo antes de sair desta tela.';

const formatCommandWaitCountdown = (totalSeconds) => {
  const sec = Math.max(0, totalSeconds);
  const minutes = Math.floor(sec / 60);
  const seconds = sec % 60;
  if (minutes > 0) {
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }
  return `${seconds}s`;
};

let socket;
let publicKey;

const Conected = (props) => {
  const history = useHistory();
  const requestRef = useRef(null);
  const imageRef = useRef(null);
  const prevImageUrlRef = useRef(null);
  const pendingCommandWaitRef = useRef(null);
  const terminalRef = useRef(null);
  const serialNumber = useParams().id;

  const user = JSON.parse(localStorage.getItem('userData'));
  const canAccessSupport = user?.permission?.support?.read === true;
  const canWriteSupport = user?.permission?.support?.write === true;
  const canCalibrar = canUseSupportCalibrar(user);
  if (!canAccessSupport) {
    window.location.replace('/home');
  }

  const [imageSrc, setImageSrc] = useState('');
  const [connectionStabished, setConnectionStabished] = useState(false)
  const [inputValueAlert, setInputValueAlert] = useState('');
  const [response, setResponse] = useState('Console\n');
  const [equipConfig, setEquipConfig] = useState({
    'serial': '--',
    'webcam': false,
    'freq_v': '--',
    'serv_e': '--',
    'serv_m': '--',
    'serv_d': '--',
    'calib_id': '--',
    'calib': '--',
    'eixo_x': '--',
    'eixo_y': '--',
    'eixo_z': '--',
    'v_firmware': '--',
    'v_firmware_osc': '--',
    'activation_date': '--',
    'activation_days': '--',
    'volume_corr_densidade': '--',
    'densidade_esfera': '--',
    'offset_bal1': '--',
    'offset_bal2': '--',
    'cross_check': '--',
    'sw': '--',
    'sws': '--'
  })

  // Estado para confirmação de ações críticas
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'calibrar' | 'reset_senha' | 'chart'
  const [updateRunning, setUpdateRunning] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [awaitingRebootConfirm, setAwaitingRebootConfirm] = useState(false);
  const [awaitingVersionConfirm, setAwaitingVersionConfirm] = useState(false);
  const [updateFailedAtIndex, setUpdateFailedAtIndex] = useState(null);
  const updateStepsRef = useRef([]);
  const updateTargetVersionRef = useRef(null);
  const [isDark, setIsDark] = useState(document.body.classList.contains('dark-theme'));
  const [commandWaitCountdown, setCommandWaitCountdown] = useState(null);
  const [commandWaitClock, setCommandWaitClock] = useState(() => Date.now());

  const clearCommandWaitCountdown = () => setCommandWaitCountdown(null);

  const commandWaitRemainingSec = commandWaitCountdown
    ? Math.max(0, Math.ceil((commandWaitCountdown.deadlineMs - commandWaitClock) / 1000))
    : null;

  /** Bloqueia saída/desconexão apenas enquanto um comando de atualização está em execução. */
  const isUpdateSessionLocked = updateRunning;
  const openConfirm = (action) => {
    setConfirmAction(action);
    setShowConfirmModal(true);
  };
  const closeConfirm = () => { setShowConfirmModal(false); setConfirmAction(null); };

  const panelClass = isDark ? 'conected-panel bg-dark text-light border-secondary' : 'conected-panel bg-light text-dark';
  const panelHeaderClass = isDark ? 'bg-dark text-light border-secondary' : 'bg-light text-dark';
  const canWrite = user?.permission?.support?.write === true && connectionStabished;

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.body.classList.contains('dark-theme'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isUpdateSessionLocked) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = UPDATE_LEAVE_MESSAGE;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUpdateSessionLocked]);

  useEffect(() => {
    if (!commandWaitCountdown) {
      return undefined;
    }

    setCommandWaitClock(Date.now());
    const intervalId = setInterval(() => setCommandWaitClock(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [commandWaitCountdown]);

  const clearPendingCommandWait = (errorMessage) => {
    const pending = pendingCommandWaitRef.current;
    if (!pending) return;

    clearTimeout(pending.timeoutId);
    pendingCommandWaitRef.current = null;
    clearCommandWaitCountdown();

    if (errorMessage) {
      pending.reject(new Error(errorMessage));
    }
  };

  const sendProcessCommand = (inputValue) => {
    if (!connectionStabished || !socket || socket.readyState !== WebSocket.OPEN) {
      throw new Error('Equipamento não conectado.');
    }

    socket.send(JSON.stringify({
      type: 'message',
      room: serialNumber,
      content: {
        command: 'process',
        parameters: {
          command: inputValue,
        },
      },
    }));
  };

  const sendCommandAndWaitForResponse = (command, {
    timeoutMs = TERMINAL_COMMAND_TIMEOUT_MS,
    onPartialResponse,
    waitLabel,
  } = {}) =>
    new Promise((resolve, reject) => {
      setCommandWaitCountdown({
        deadlineMs: Date.now() + timeoutMs,
        label: waitLabel || 'Aguardando resposta do equipamento',
      });

      const timeoutId = setTimeout(() => {
        const pending = pendingCommandWaitRef.current;
        if (pending?.resolve === resolve) {
          const partialOutput = joinResponseChunks(pending.chunks || []);
          pendingCommandWaitRef.current = null;
          clearCommandWaitCountdown();
          reject(Object.assign(new Error('Equipamento não respondeu (timeout).'), { partialOutput }));
        }
      }, timeoutMs);

      pendingCommandWaitRef.current = {
        chunks: [],
        resolve,
        reject,
        timeoutId,
        onPartialResponse,
      };

      try {
        if (terminalRef.current) {
          const sent = terminalRef.current.executeCommand(command);
          if (!sent) {
            throw new Error('Comando bloqueado pelo terminal.');
          }
        } else {
          sendProcessCommand(command);
        }
      } catch (err) {
        clearTimeout(timeoutId);
        pendingCommandWaitRef.current = null;
        clearCommandWaitCountdown();
        reject(err);
      }
    });

  const handleEquipmentTextMessage = (text) => {
    const pending = pendingCommandWaitRef.current;

    if (pending) {
      pending.chunks.push(text);
      const accumulated = joinResponseChunks(pending.chunks);
      pending.onPartialResponse?.(accumulated);

      if (isCommandResponseComplete(accumulated)) {
        clearTimeout(pending.timeoutId);
        pendingCommandWaitRef.current = null;
        clearCommandWaitCountdown();
        setResponse(accumulated);
        pending.resolve(accumulated);
      }
      return;
    }

    setResponse(text);
  };

  const handleSubmit = (inputValue) => {
    if (!connectionStabished) return;
    sendProcessCommand(inputValue);
  };

  const parseStepResponse = (equipmentResponse, step) => parseEquipmentUpdateOutput(equipmentResponse, step.id, {
    abortOnFailure: step.abortOnFailure,
    expectedLatest: step.id === 9 ? updateTargetVersionRef.current : null,
  });

  const runUpdateSteps = async (steps, startIndex = 0, { includesReboot = false } = {}) => {
    for (let index = startIndex; index < steps.length; index += 1) {
      const step = steps[index];
      setUpdateFailedAtIndex(null);

      if (step.expectDisconnect) {
        terminalRef.current?.executeCommand(step.command) ?? sendProcessCommand(step.command);
        setResponse(
          'Equipamento reiniciando. Você pode sair desta tela e reconectar depois; '
          + 'use «Confirmar pós-reboot» quando voltar.',
        );
        setAwaitingVersionConfirm(false);
        setAwaitingRebootConfirm(true);
        return;
      }

      const timeoutMs = step.timeoutMs ?? getStepTimeoutMs(step.id);
      let equipmentResponse;

      try {
        equipmentResponse = await sendCommandAndWaitForResponse(step.command, {
          timeoutMs,
          waitLabel: `Passo ${step.id}: ${step.title}`,
          onPartialResponse: (accumulated) => {
            setResponse(accumulated);
          },
        });
      } catch (err) {
        if (err?.partialOutput) {
          setResponse(err.partialOutput);
        }
        if (step.id === 6) {
          setResponse(
            'Reiniciando serviços PM2 no equipamento.\n'
            + 'Aguarde alguns segundos enquanto os processos sobem novamente...',
          );
          await new Promise((resolve) => { setTimeout(resolve, PM2_RESTART_WAIT_MS); });
          continue;
        }
        throw err;
      }

      const parsed = parseStepResponse(equipmentResponse, step);

      if (step.id === 1 && parsed.meta.latestVersion) {
        updateTargetVersionRef.current = parsed.meta.latestVersion;
      }

      if (!canAdvanceAfterResponse(parsed)) {
        setUpdateFailedAtIndex(index);
        throw new Error(parsed.messages.join(' ') || `Falha no passo ${step.id}: ${step.title}.`);
      }

      if (step.id === 6) {
        setResponse(
          'Reiniciando serviços PM2 no equipamento.\n'
          + 'Aguarde alguns segundos enquanto os processos sobem novamente...',
        );
        await new Promise((resolve) => { setTimeout(resolve, PM2_RESTART_WAIT_MS); });
      }
    }

    if (!includesReboot) {
      setAwaitingVersionConfirm(true);
      setResponse(
        '[OK] Passos 1–6 concluídos.\n'
        + 'Clique em «Confirmar versão» para validar sw-mt / stable e PM2 (passo 9).\n'
        + 'Você já pode sair desta tela ou desconectar; reconecte depois para confirmar a versão, se preferir.',
      );
      setUpdateError('');
      setUpdateFailedAtIndex(null);
    }
  };

  const handleAtualizarEquipamento = async ({ fromIndex = 0 } = {}) => {
    if (!canWriteSupport || !connectionStabished || updateRunning) return;

    const steps = fromIndex === 0
      ? buildEquipmentUpdateSteps({
        includeClearCurves: false,
        includeReboot: false,
      })
      : updateStepsRef.current;

    if (!steps.length) return;

    updateStepsRef.current = steps;

    if (fromIndex === 0) {
      setUpdateError('');
      setAwaitingRebootConfirm(false);
      setAwaitingVersionConfirm(false);
      updateTargetVersionRef.current = null;
      setUpdateFailedAtIndex(null);
      setResponse('Iniciando atualização automática...\n');
    } else {
      setUpdateError('');
      setResponse(`Retomando atualização a partir do passo ${steps[fromIndex].id}...\n`);
    }

    setUpdateRunning(true);

    try {
      await runUpdateSteps(steps, fromIndex, { includesReboot: false });
    } catch (err) {
      let message = err?.message || 'Falha ao atualizar equipamento.';
      if (err?.partialOutput) {
        const partial = String(err.partialOutput);
        if (!isCommandResponseComplete(partial)) {
          message += '\n\nSaída parcial no timeout (veja o terminal acima).';
          if (/Iniciando fetch\.\.\./i.test(partial) && !/fetch exit:/i.test(partial)) {
            message += '\nProvável travamento no git fetch (rede ou repositório).';
          }
        }
        setResponse(`${partial}\n\n[FALHA] ${message}`);
      } else {
        setResponse(`[FALHA] ${message}`);
      }
      setUpdateError(message.split('\n')[0]);
      setAwaitingVersionConfirm(false);
    } finally {
      setUpdateRunning(false);
    }
  };

  const handleRetryUpdate = () => {
    if (!canWriteSupport || updateFailedAtIndex == null || updateRunning) return;
    handleAtualizarEquipamento({ fromIndex: updateFailedAtIndex });
  };

  const handleConfirmInstalledVersion = async () => {
    if (!canWriteSupport || !connectionStabished || updateRunning) return;
    if (!awaitingRebootConfirm && !awaitingVersionConfirm) return;

    const step = POST_REBOOT_STEP;

    setUpdateRunning(true);
    setUpdateError('');

    try {
      const equipmentResponse = await sendCommandAndWaitForResponse(step.command, {
        timeoutMs: step.timeoutMs ?? getStepTimeoutMs(step.id),
        waitLabel: `Passo ${step.id}: ${step.title}`,
      });
      const parsed = parseStepResponse(equipmentResponse, step);

      if (!parsed.validation?.ok) {
        throw new Error(parsed.messages.join(' ') || 'Falha na confirmação da versão (passo 9).');
      }

      const version = parsed.meta.confirmedVersion || parsed.validation?.version;
      setAwaitingRebootConfirm(false);
      setAwaitingVersionConfirm(false);
      setUpdateFailedAtIndex(null);
      setResponse(
        version
          ? `[OK] Versão confirmada: ${version}${updateTargetVersionRef.current ? ` (alvo: ${updateTargetVersionRef.current})` : ''}.\n`
            + 'Atualização concluída. Você pode desconectar ou sair desta tela.'
          : '[OK] Confirmação concluída.\nAtualização concluída. Você pode desconectar ou sair desta tela.',
      );
    } catch (err) {
      const message = err?.message || 'Falha na confirmação da versão.';
      setUpdateError(message);
      setResponse(`[FALHA] ${message}`);
    } finally {
      setUpdateRunning(false);
    }
  };

  const handleSubmitAlert = (event) => {
    if (!inputValueAlert || !connectionStabished) return;

    event.preventDefault();
    const command = {
      type: 'alert',
      room: serialNumber,
      msg: inputValueAlert
    };
    socket.send(JSON.stringify(command));

    setInputValueAlert('');
  };

  const base64ToBlob = (base64, mime = 'image/png') => {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mime });
  };

  const handleNewFrame = (imgData) => {
    requestRef.current = requestAnimationFrame(() => {
      try {
        const blob = base64ToBlob(imgData, 'image/png');
        if (prevImageUrlRef.current) {
          URL.revokeObjectURL(prevImageUrlRef.current);
        }
        const objectUrl = URL.createObjectURL(blob);
        prevImageUrlRef.current = objectUrl;
        setImageSrc(objectUrl);
      } catch (err) {
        setImageSrc(`data:image/png;base64,${imgData}`);
      }
    });
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      // update ratios if other logic depends on them
      // ...existing code...
    }
  };

  const handleConnect = () => {
    let pingInterval
    // Criar uma nova instância WebSocket
    socket = new WebSocket(wsUrl);

    pingInterval = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, 20000);

    socket.onopen = () => {
      setConnectionStabished(true)
    };

    socket.onmessage = (event) => {
      const data = event.data;
      let json_msg = JSON.parse(data);

      if (json_msg.type === "invalid_user") {
        if (json_msg?.msg) {
          alert(json_msg?.msg)
        } else {
          alert("Sem permissão!");
        }
        handleDisconnect({ force: true });
      }

      if (json_msg.type === "valid_user") {
        setConnectionStabished(true)
        handleLoadData()
      }

      if (json_msg.type === "desconnect") {
        setConnectionStabished(false);
        clearPendingCommandWait('Conexão encerrada.');
        clearCommandWaitCountdown();
        setUpdateRunning(false);
        setAwaitingVersionConfirm(false);
      }

      if (json_msg.type === "connected") {
        setConnectionStabished(true)
        handleLoadData()
      }

      if (json_msg.pubkey) {
        publicKey = json_msg.pubkey;

        socket.send(
          JSON.stringify({
            type: 'join',
            room: serialNumber,
            user_id: user?._id,
            name: user?.name,
            email: user?.email,
            client: 'client',
            page: 'support',
            token: getToken(),
            content: {
              name: `${user.email}-API-SUPORTE`
            },
          }));

      } else {
        if (json_msg?.content?.img) {
          handleNewFrame(json_msg.content.img);
          return;
        }

        if (json_msg?.content?.text) {
          const text = json_msg.content.text;
          requestAnimationFrame(() => {
            handleEquipmentTextMessage(text);
          });
          return;
        }

        if (json_msg?.content?.network_config) {
          // setNetworkConfig(json_msg.content.network_config);
          return;
        }

        if (json_msg?.content?.equip_config) {
          requestAnimationFrame(() => {
            setEquipConfig(json_msg.content.equip_config);
          });
          return;
        }
      }
    };

    socket.onclose = () => {
      clearInterval(pingInterval)
    };
  };

  const handleLoadData = () => {
    socket.send(
      JSON.stringify({
        type: 'message',
        room: serialNumber,
        content: {
          command: 'load_data',
          parameters: {}
        },
      })
    );

    socket.send(
      JSON.stringify({
        type: 'connect_support',
        room: serialNumber,
        hidden: user.master ? user.master : false
      })
    );
  }

  const handleImageClick = (event) => {
    if (!connectionStabished) return;

    const rect = event.target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    socket.send(
      JSON.stringify({
        type: 'message',
        room: serialNumber,
        content: {
          command: 'click',
          parameters: {
            x: x,
            y: y
          }
        }
      })
    );
  };

  const sendGetScreen = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: 'connect_support',
          room: serialNumber,
          stream_internal_cam: false,
          hidden: user.master ? user.master : false
        })
      );
    }
  };

  useEffect(() => {
    const interval1 = setInterval(() => {
      sendGetScreen();
    }, refreshRate);
    return () => {
      clearInterval(interval1);
    };
  }, [socket]);

  const requestDisconnect = ({ force = false } = {}) => {
    if (!force && isUpdateSessionLocked) {
      return;
    }

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: 'desconnect_support',
          room: serialNumber
        })
      );
      socket.close();
      setConnectionStabished(false);
    }
    history.push('/support');
  };

  const handleDisconnect = (options) => requestDisconnect(options);

  useEffect(() => {
    handleConnect();
    return () => {
      clearPendingCommandWait('Conexão encerrada.');
      if (socket) socket.close();
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
      if (prevImageUrlRef.current) {
        URL.revokeObjectURL(prevImageUrlRef.current);
      }
    };
  }, []);

  const handleChart = () => {
    if (!connectionStabished) return;

    // É mais seguro e legível montar o comando Python separado.

    //home/orangepi/venv/bin/python -c "from db import getInstance as DB; DB().clear_charts('CUSTOM'); DB().clear_charts('COMPLEMENT'), DB().clear_charts('INMETRO'); DB().conn.commit()"

    const pythonScriptCommand = `from db import getInstance as DB; DB().clear_charts('CUSTOM'); DB().clear_charts('COMPLEMENT'), DB().clear_charts('INMETRO'); DB().conn.commit()`;
    const fullShellCommand = `/home/orangepi/venv/bin/python -c "${pythonScriptCommand}"`;

    const command = {
      type: 'message',
      room: serialNumber,
      content: {
        command: 'process',
        parameters: {
          command: fullShellCommand
        }
      }
    };
    socket.send(JSON.stringify(command));
  }

  const handleCalibrar = () => {
    if (!canCalibrar || !connectionStabished) return;

    // Função auxiliar para gerar SHA-256 (hex) usando Web Crypto API
    const sha256Hex = async (text) => {
      try {
        const encoder = new TextEncoder();
        const data = encoder.encode(text);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
        return Array.from(new Uint8Array(hashBuffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
      } catch (err) {
        console.error('Falha ao gerar SHA-256', err);
        return null;
      }
    };

    (async () => {
      const raw = `${serialNumber}Mtc`;
      const hash = await sha256Hex(raw);
      if (!hash) {
        alert('Não foi possível gerar hash para calibração.');
        return;
      }
      // Monta comando curl conforme solicitado
      const fullShellCommand = `curl -X GET http://localhost:3000/api/v1/atualizacalibracao/${hash}`;

      const command = {
        type: 'message',
        room: serialNumber,
        content: {
          command: 'process',
          parameters: {
            command: fullShellCommand
          }
        }
      };
      socket.send(JSON.stringify(command));
    })();
  }

  const handleResetSenha = () => {
    if (!connectionStabished) return;

    const fullShellCommand = resetPasswordCommand;

    const command = {
      type: 'message',
      room: serialNumber,
      content: {
        command: 'process',
        parameters: {
          command: fullShellCommand
        }
      }
    };
    socket.send(JSON.stringify(command));
  }

  return (
    <Page className="p-3" title="Suporte">
      <Prompt when={isUpdateSessionLocked} message={UPDATE_LEAVE_MESSAGE} />

      <Header page={"Suporte"} handleDisconnect={handleDisconnect} />

      {isUpdateSessionLocked && (
        <Alert variant="warning" className="mb-2 py-2 conected-update-lock-alert">
          <div>{UPDATE_LEAVE_MESSAGE}</div>
          {commandWaitRemainingSec != null && (
            <div className="small mt-1 mb-0 fw-semibold">
              {commandWaitCountdown.label}
              {' — '}
              timeout em{' '}
              <span className="font-monospace">{formatCommandWaitCountdown(commandWaitRemainingSec)}</span>
            </div>
          )}
        </Alert>
      )}

      <Card className={`mb-3 mt-2 ${panelClass}`}>
        <Card.Body className="py-2 px-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3">
              <span className="conected-session-serial fw-semibold">{serialNumber}</span>
              <Badge
                bg={connectionStabished ? 'success' : 'danger'}
                className="conected-status-badge"
              >
                {connectionStabished ? 'Conectado' : 'Desconectado'}
              </Badge>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2">
              {canWriteSupport && (
                <>
                  <Button
                    size="sm"
                    variant="outline-light"
                    onClick={handleAtualizarEquipamento}
                    disabled={!connectionStabished || updateRunning}
                  >
                    Atualizar equipamento
                  </Button>
                  {updateError && !updateRunning && updateFailedAtIndex != null && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={handleRetryUpdate}
                      disabled={!connectionStabished}
                    >
                      Tentar novamente
                    </Button>
                  )}
                  {(awaitingRebootConfirm || awaitingVersionConfirm) && (
                    <Button
                      size="sm"
                      variant="outline-warning"
                      onClick={handleConfirmInstalledVersion}
                      disabled={!connectionStabished || updateRunning}
                    >
                      {awaitingRebootConfirm ? 'Confirmar pós-reboot' : 'Confirmar versão'}
                    </Button>
                  )}
                  {canCalibrar && (
                    <Button
                      size="sm"
                      className="conected-btn-brand"
                      onClick={() => openConfirm('calibrar')}
                      disabled={!connectionStabished}
                    >
                      Calibrar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="conected-btn-warning"
                    onClick={() => openConfirm('reset_senha')}
                    disabled={!connectionStabished}
                  >
                    Resetar senha
                  </Button>
                  <div className="conected-actions-divider d-none d-sm-block" />
                </>
              )}
              <Button
                size="sm"
                className="conected-btn-danger"
                onClick={() => handleDisconnect()}
                disabled={isUpdateSessionLocked}
                title={isUpdateSessionLocked ? UPDATE_LEAVE_MESSAGE : undefined}
              >
                Desconectar
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className={`mb-3 p-2 ${panelClass}`}>
        <div className="conected-stream-terminal">
          <div className="conected-stream-terminal__screen">
            {!imageSrc ? (
              <div className="conected-stream-placeholder">
                Aguardando imagem...
              </div>
            ) : (
              <img
                className="conected-stream-image"
                onClick={handleImageClick}
                src={imageSrc}
                alt="Stream remoto do equipamento"
                style={{
                  cursor: connectionStabished ? 'pointer' : 'none',
                  opacity: connectionStabished ? 1 : 0.7,
                  filter: connectionStabished ? 'none' : 'grayscale(100%)',
                }}
              />
            )}
          </div>

          <div className="conected-stream-terminal__terminal">
            <Terminal
              ref={terminalRef}
              onCommandSubmit={handleSubmit}
              response={response}
              connectionStabished={connectionStabished}
              permission={user?.permission?.support?.write}
              inputDisabled={updateRunning}
            />
          </div>
        </div>
      </Card>

      <Row className="g-3">
        <Col lg={6}>
          <Card className={`h-100 ${panelClass}`}>
            <Card.Header className={panelHeaderClass}>Configuração do equipamento</Card.Header>
            <Card.Body className="p-2">
              <Table
                className={`mb-0 ${isDark ? 'table-dark' : ''}`}
                style={{ fontSize: '0.85rem' }}
                striped
                bordered
                responsive
                size="sm"
              >
                <thead>
                  <tr>
                    <th className="text-center">SERIAL</th>
                    <th className="text-center">FREQUÊNCIA (V)</th>
                    <th className="text-center">ID CALIBRAÇÃO</th>
                    <th className="text-center">WEBCAM</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-center">{equipConfig.serial}</td>
                    <td className="text-center">{equipConfig.freq_v}</td>
                    <td className="text-center">{equipConfig.calib_id}</td>
                    <td className="text-center">{equipConfig.webcam ? 'Sim' : 'Não'}</td>
                  </tr>
                </tbody>
                <thead>
                  <tr>
                    <th className="text-center">DATA DE ATIVAÇÃO</th>
                    <th className="text-center">DIAS DE ATIVAÇÃO</th>
                    <th className="text-center">SOFTWARE</th>
                    <th className="text-center">SOFTWARE BACKUP</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-center">{equipConfig.activation_date ? dateformat(equipConfig.activation_date, true) : '--'}</td>
                    <td className="text-center">{equipConfig.activation_days}</td>
                    <td className="text-center">{equipConfig.sw}</td>
                    <td className="text-center">{equipConfig.sws}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className={`h-100 ${panelClass}`}>
            <Card.Header className={panelHeaderClass}>Alerta ao equipamento</Card.Header>
            <Card.Body className="p-3 d-flex flex-column gap-2">
              <Form.Control
                onChange={(e) => setInputValueAlert(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmitAlert(e);
                  }
                }}
                type="text"
                placeholder="Mensagem de alerta..."
                value={inputValueAlert}
                disabled={!canWrite}
              />
              <Button
                onClick={handleSubmitAlert}
                type="button"
                className="conected-btn-success"
                disabled={!canWrite}
              >
                Enviar alerta
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showConfirmModal}
        onHide={closeConfirm}
        centered
        backdrop="static"
        contentClassName={isDark ? 'bg-dark text-light border-secondary' : 'bg-white'}
      >
        <Modal.Header
          closeButton
          className={isDark ? 'bg-dark text-light border-secondary' : undefined}
          style={{ borderBottom: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }}
        >
          <Modal.Title className="fw-semibold" style={{ fontSize: '1rem' }}>Confirmação</Modal.Title>
        </Modal.Header>
        <Modal.Body
          className={isDark ? 'bg-dark text-light' : undefined}
          style={{ background: isDark ? '#1f2428' : '#fff' }}
        >
          {confirmAction === 'calibrar' && (
            <p className="mb-0">Tem certeza que deseja executar a calibração? Esta ação enviará um comando remoto ao equipamento.</p>
          )}
          {confirmAction === 'reset_senha' && (
            <p className="mb-0">Tem certeza que deseja resetar a senha? Esta ação irá limpar/definir uma senha vazia no equipamento.</p>
          )}
          {confirmAction === 'chart' && (
            <p className="mb-0">Confirmar atualização forçada das curvas? Isso pode limpar ou regenerar dados de gráfico.</p>
          )}
        </Modal.Body>
        <Modal.Footer
          className={isDark ? 'bg-dark text-light border-secondary' : undefined}
          style={{ borderTop: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }}
        >
          <Button
            onClick={closeConfirm}
            style={{
              backgroundColor: 'transparent',
              color: isDark ? '#fff' : '#6c757d',
              border: '1px solid',
              borderColor: isDark ? '#fff' : '#6c757d'
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (confirmAction === 'calibrar') {
                closeConfirm();
                handleCalibrar();
              } else if (confirmAction === 'reset_senha') {
                closeConfirm();
                handleResetSenha();
              } else if (confirmAction === 'chart') {
                closeConfirm();
                handleChart();
              } else {
                closeConfirm();
              }
            }}
            className="conected-btn-brand"
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
    </Page>
  );
};

export default Conected;
